import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ContentLoaderPlaceholder from '@welldev-group/rcl-content-loader-placeholder';
import { I18nText, PillTabs } from '@welldev-group/react-comp-lib';
import { StringParam, withDefault, useQueryParams } from 'use-query-params';
import withQueryParamsProvider from '../../../shared/components/withQueryParamsProvider';
import routes from '../../../shared/routes';
import httpClient from '../../../shared/libraries/httpClient';
import EmptyTableContent from '../../../shared/components/EmptyTableContent';

import TransactionTable from './TransactionTable';
import InvoicesTable from './InvoicesTable';

const TAB_MAPPER = {
  transactions: 0,
  invoices: 1,
};

const TransactionListOverview = ({ affiliateId, canRecordWireTransfer, fromAffiliate360 }) => {
  const [transactions, setTransactions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(true);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(true);
  const [selectedTabKey, setSelectedTabKey] = useState(TAB_MAPPER.transactions);

  const [query, setQuery] = useQueryParams({
    tab: withDefault(StringParam, ''),
  });

  const { tab } = query;

  const fetchTransactions = async () => {
    const { data } = await httpClient.get(
      routes.billing.transactions.list({ affiliate_id: affiliateId }),
    );
    setTransactions(data.transactions?.slice(0, 5));
    setIsLoadingTransactions(false);
  };

  const fetchOpenInvoices = async () => {
    const { data } = await httpClient.get(
      routes.billing.invoices.list({
        state: 'open',
        affiliate_id: affiliateId,
      }),
    );
    setInvoices(data.invoices.slice(0, 5));
    setIsLoadingInvoices(false);
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    if (selectedTabKey === 0) {
      setIsLoadingTransactions(true);
      fetchTransactions();
    } else {
      setIsLoadingInvoices(true);
      fetchOpenInvoices();
    }
  }, [selectedTabKey]);

  useEffect(() => {
    if (fromAffiliate360) {
      setQuery({ tab: 'payments' }, 'replaceIn');
    }
  }, [tab]);

  const pendingInvoicesContent = (
    <div className="col-12 col-bleed">
      {isLoadingInvoices ? (
        <ContentLoaderPlaceholder numberOfLines={10} />
      ) : (
        <>
          {invoices.length > 0 && (
            <InvoicesTable
              invoices={invoices}
              canRecordWireTransfer={canRecordWireTransfer}
              showAll={false}
            />
          )}
        </>
      )}
      {!isLoadingInvoices && !invoices.length && <EmptyTableContent />}
    </div>
  );

  const transactionContent = (
    <div className="col-12 col-bleed col-grid billing-transaction-list__transactions">
      {isLoadingTransactions ? (
        <ContentLoaderPlaceholder numberOfLines={10} />
      ) : (
        <>
          {transactions?.length > 0 && (
            <>
              <TransactionTable transactions={transactions} isOverview={true} showAll={false} />
            </>
          )}
        </>
      )}
      {!isLoadingTransactions && !transactions?.length && <EmptyTableContent />}
    </div>
  );

  const setSelectedTabKeyOnClick = tabindex => {
    setSelectedTabKey(tabindex);
  };

  const tabs = [
    {
      key: 0,
      title: <I18nText id="billing.transactions.list.transactions_title" />,

      getContent: () => transactionContent,
    },
    {
      key: 1,
      title: <I18nText id="billing.transactions.list.open_invoices_header" />,
      getContent: () => pendingInvoicesContent,
    },
  ];

  return (
    <>
      <div className="booking-with-accountant">
        <PillTabs
          selectedTabKey={selectedTabKey}
          items={tabs}
          onChange={setSelectedTabKeyOnClick}
        />
      </div>
    </>
  );
};

TransactionListOverview.defaultProps = {
  canRecordWireTransfer: false,
  fromAffiliate360: false,
};

TransactionListOverview.propTypes = {
  affiliateId: PropTypes.number.isRequired,
  canRecordWireTransfer: PropTypes.bool,
  fromAffiliate360: PropTypes.bool,
};

export default withQueryParamsProvider(TransactionListOverview);
